import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ReadingSettingsProps {
  showFurigana: boolean;
  setShowFurigana: (value: boolean) => void;
  showRomaji: boolean;
  setShowRomaji: (value: boolean) => void;
  showChinese: boolean;
  setShowChinese: (value: boolean) => void;
  hideUnderline: boolean;
  setHideUnderline: (value: boolean) => void;
  hideGrammarBackground: boolean;
  setHideGrammarBackground: (value: boolean) => void;
  hideShadowingSpotlight: boolean;
  setHideShadowingSpotlight: (value: boolean) => void;
  showOnlyChinese: boolean;
  setShowOnlyChinese: (value: boolean) => void;
  highlightNewWords: boolean;
  setHighlightNewWords: (value: boolean) => void;
  fontSize: number;
  setFontSize: (value: number) => void;
  blurStrength: number;
  setBlurStrength: (value: number) => void;
  theme: string;
  setTheme: (value: string) => void;
  volume?: number;
  setVolume?: (value: number) => void;
  autoPlayNext?: boolean;
  setAutoPlayNext?: (value: boolean) => void;
  isMobile?: boolean;
  onClose: () => void;
}

export function ReadingSettings({
  showFurigana,
  setShowFurigana,
  showRomaji,
  setShowRomaji,
  showChinese,
  setShowChinese,
  hideUnderline,
  setHideUnderline,
  hideGrammarBackground,
  setHideGrammarBackground,
  hideShadowingSpotlight,
  setHideShadowingSpotlight,
  showOnlyChinese,
  setShowOnlyChinese,
  highlightNewWords,
  setHighlightNewWords,
  fontSize,
  setFontSize,
  blurStrength,
  setBlurStrength,
  theme,
  setTheme,
  volume,
  setVolume,
  autoPlayNext,
  setAutoPlayNext,
  isMobile = false,
  onClose,
}: ReadingSettingsProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-xl antialiased",
        "w-full max-w-[420px] sm:w-[340px]",
      )}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-base font-bold tracking-tight text-foreground dark:text-white">播放设置</h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClose}
          title="关闭设置"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div
        className={cn(
          "space-y-4 overflow-y-auto p-4",
          isMobile ? "max-h-[72dvh] pb-[max(0.75rem,env(safe-area-inset-bottom))]" : "max-h-[72vh]",
        )}
      >
        <div className="space-y-2 rounded-lg border border-border/60 bg-background/60 p-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold text-foreground dark:text-white">字号</Label>
            <span className="text-xs text-muted-foreground">{fontSize.toFixed(1)}x</span>
          </div>
          <Slider
            value={[fontSize]}
            min={0.8}
            max={2}
            step={0.1}
            onValueChange={(value) => setFontSize(value[0])}
          />
        </div>

        <div className="space-y-2 rounded-lg border border-border/60 bg-background/60 p-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold text-foreground dark:text-white">模糊遮罩</Label>
            <span className="text-xs text-muted-foreground">
              {blurStrength > 0 ? `${blurStrength}px` : "Off"}
            </span>
          </div>
          <Slider
            value={[blurStrength]}
            min={0}
            max={10}
            step={1}
            onValueChange={(value) => setBlurStrength(value[0])}
          />
        </div>

        {typeof volume === "number" && setVolume ? (
          <div className="space-y-2 rounded-lg border border-border/60 bg-background/60 p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-foreground dark:text-white">音量</Label>
              <span className="text-xs text-muted-foreground">{Math.round(volume * 100)}%</span>
            </div>
            <Slider
              value={[volume]}
              min={0}
              max={1}
              step={0.05}
              onValueChange={(value) => setVolume(value[0])}
            />
          </div>
        ) : null}

        <div className="space-y-2 rounded-lg border border-border/60 bg-background/60 p-3">
          <Label className="text-sm font-semibold text-foreground dark:text-white">显示选项</Label>

          <div className="grid gap-2 pt-1">
            <div className="flex items-center space-x-2">
              <Checkbox id="show-furigana" checked={showFurigana} onCheckedChange={(c) => setShowFurigana(!!c)} />
              <Label htmlFor="show-furigana" className="cursor-pointer text-sm text-foreground dark:text-white">显示振假名</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="show-romaji" checked={showRomaji} onCheckedChange={(c) => setShowRomaji(!!c)} />
              <Label htmlFor="show-romaji" className="cursor-pointer text-sm text-foreground dark:text-white">显示罗马音</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="show-chinese" checked={showChinese} onCheckedChange={(c) => setShowChinese(!!c)} />
              <Label htmlFor="show-chinese" className="cursor-pointer text-sm text-foreground dark:text-white">显示中文翻译</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="hide-underline" checked={hideUnderline} onCheckedChange={(c) => setHideUnderline(!!c)} />
              <Label htmlFor="hide-underline" className="cursor-pointer text-sm text-foreground dark:text-white">隐藏词义下划线</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-grammar-bg"
                checked={hideGrammarBackground}
                onCheckedChange={(c) => setHideGrammarBackground(!!c)}
              />
              <Label htmlFor="hide-grammar-bg" className="cursor-pointer text-sm text-foreground dark:text-white">隐藏语法底色</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-spotlight"
                checked={hideShadowingSpotlight}
                onCheckedChange={(c) => setHideShadowingSpotlight(!!c)}
              />
              <Label htmlFor="hide-spotlight" className="cursor-pointer text-sm text-foreground dark:text-white">隐藏跟读聚光</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="only-chinese" checked={showOnlyChinese} onCheckedChange={(c) => setShowOnlyChinese(!!c)} />
              <Label htmlFor="only-chinese" className="cursor-pointer text-sm text-foreground dark:text-white">仅显示中文</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="highlight-words" checked={highlightNewWords} onCheckedChange={(c) => setHighlightNewWords(!!c)} />
              <Label htmlFor="highlight-words" className="cursor-pointer text-sm font-semibold text-blue-600 dark:text-blue-300">生词高亮</Label>
            </div>
          </div>
        </div>

        {typeof autoPlayNext === "boolean" && setAutoPlayNext ? (
          <div className="space-y-2 rounded-lg border border-border/60 bg-background/60 p-3">
            <Label className="text-sm font-semibold text-foreground dark:text-white">播放顺序</Label>
            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="auto-play-next"
                checked={autoPlayNext}
                onCheckedChange={(c) => setAutoPlayNext(!!c)}
              />
              <Label htmlFor="auto-play-next" className="cursor-pointer text-sm text-foreground dark:text-white">
                自动连播
              </Label>
            </div>
          </div>
        ) : null}

        <div className="space-y-2 rounded-lg border border-border/60 bg-background/60 p-3">
          <Label className="text-sm font-semibold text-foreground dark:text-white">主题</Label>
          <Select value={theme} onValueChange={setTheme}>
            <SelectTrigger>
              <SelectValue placeholder="选择主题" />
            </SelectTrigger>
            <SelectContent className="z-[130]">
              <SelectItem value="system">跟随系统</SelectItem>
              <SelectItem value="light">浅色</SelectItem>
              <SelectItem value="dark">深色</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button type="button" className="w-full" onClick={onClose}>
          完成
        </Button>
      </div>
    </div>
  );
}
